import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaSchool, FaEye } from "react-icons/fa";
import styles from "./List.module.css";
import { CustomButton } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { useSelector } from "react-redux";

const ClassSectionList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Get class data from location state
  const { translations } = useSelector((state) => state.language);
  const { classId, className } = location.state || {};
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    if (classId) {
      fetchSectionList();
    }
  }, [classId]);

  // API call to fetch section list
  const fetchSectionList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(`/school/class/viewList`, {
        id: classId,
        school_id: school_id,
        // id: 66,
      });

      if (response?.data?.success) {
        const apiData = response.data.data || [];

        // Transform API data to match table structure
        const transformedData = apiData.map((item) => ({
          id: item.id,
          class: className,
          section: item.section_id,
          classTeacher: item.staff_name || "-",
          total_boys: item.total_boys || 0,
          total_girls: item.total_girls || 0,
          total_students: item.total_students || 0,
          shiftType: item.shift_type,
          classStaff: item.class_staff,
          groupId: item.group_id,
          isActive: item.isActive,
        }));

        setData(transformedData);
        setRowCount(transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching section list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!apiSearch) return data;
    const searchLower = apiSearch.toLowerCase();
    return data.filter(
      (item) =>
        item.class?.toLowerCase().includes(searchLower) ||
        item.section?.toLowerCase().includes(searchLower) ||
        item.classTeacher?.toLowerCase().includes(searchLower) ||
        item.total_boys?.toString().includes(searchLower) ||
        item.total_girls?.toString().includes(searchLower) ||
        item.total_students?.toString().includes(searchLower),
    );
  }, [apiSearch, data]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination.pageIndex, pagination.pageSize]);

  // Handle Add Class button
  const handleAddClass = () => {
    navigate("/user/classCreate", {
      state: {
        classId: classId,
        className: className,
      },
    });
  };

  // Handle view action
  const handleView = (row) => {
    navigate("/user/classCreate", {
      state: {
        classId: classId,
        className: className,
        sectionId: row.id,
        sectionData: row,
        isView: true,
      },
    });
  };

  // Table columns - Class, Section, Class Teacher, No. of Male, No. of Female, Total Students, Actions
  const columns = useMemo(
    () => [
      {
        accessorKey: "class",
        header: "Class",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "section",
        header: "Section",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "classTeacher",
        header: "Class Teacher",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "total_boys",
        header: "No. of Male",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "total_girls",
        header: "No. of Female",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "total_students",
        header: "Total No. of Students",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title="View"
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [classId, className],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaSchool />
        </div>
        <h1 className={styles.headerTitle}>Class {className} - Section List</h1>
      </div>

      {/* Actions - Add Class Button */}
      <div className={styles.actions}>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.addButton}
          fullWidth={false}
          onClick={handleAddClass}
        >
          Add Section
        </CustomButton>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        pagination={pagination}
        rowCount={filteredData.length}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        placeholder={translations.labels.searchButton}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
      />
    </div>
  );
};

export default ClassSectionList;
